'use client';

import { useEffect, useState } from "react";
import HeroSection from "@/sections/HeroSection";
import FacilitiesSection from "@/sections/FacilitiesSection";
import ServiceIntroductionSection from "@/sections/ServiceIntroductionSection";
import OfferedServicesSection from "@/sections/OfferedServicesSection";
import TestimonialCard from "@/components/ui/TestimonialCard/TestimonialCard";
import DevicesSection from "@/sections/DevicesSection";
import { BrandInfoSectionContent, CTASectionContent, LanguageType, NavbarSectionContent, ServiceCategorySectionContent, BeautyDeviceSectionContent, PopUpSectionContent, HeroSectionContent } from "@/lib/types";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import ModalContainer from "@/components/ui/ModalContainer/ModalContainer";
import Popup from "@/components/ui/Popup/Popup";
import { usePreventScroll } from "@/hooks/usePreventScroll";
import NavbarContainer from "@/components/client/NavbarContainer";
import AnchorNavWrapper from "@/components/ui/AnchorNavWrapper";
import FooterWrapper from "@/components/ui/FooterWrapper";
import { PageData } from "@/lib/types";
import {
  Armchair,
  Coffee,
  EyeSlash,
  CoatHanger,
  Lockers,
  Shower,
  Gift,
  ShieldStar,
  HandSoap,
  LetterCircleP
} from "@phosphor-icons/react";
import { useLoading } from "@/contexts/LoadingContext";
import useAspectRatio from '@/hooks/useAspectRatio';
import { useAnimatedNavigate } from "@/hooks/useAnimatedNavigation";

import { debug } from '@/lib/debug';
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';
import { setLanguage } from '@/lib/language';
gsap.registerPlugin(useGSAP, ScrollTrigger);


// TODO: sun transition does not work perfectly on some pages. the range of action is to small. s

interface ServicesPageClientProps {
  pageData: PageData | null;
  initialLanguage: LanguageType;
}

export default function ServicesPageClient({ pageData, initialLanguage }: ServicesPageClientProps) {
  const language: LanguageType = initialLanguage;
  // const isLg = useIsLg();
  const isTb = useIsTb();
  const [showCartPopup, setShowCartPopup] = useState(false);
  const animatedNavigate = useAnimatedNavigate();
  const aspectRatio = useAspectRatio();
  const { setAspectRatioReady } = useLoading();

  debug.log('rerender ServicesPage')

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('navigationTransitionReady'));
  }, []);



  // Notify loading system when aspect ratio is ready
  useEffect(() => {
    if (aspectRatio !== 0) {
      setAspectRatioReady(true);
    }
  }, [aspectRatio, setAspectRatioReady]);


  usePreventScroll(showCartPopup);

  if (!pageData) {
    debug.log('--------------------------------');
    debug.log('PAGE DATA IS NULL');
    debug.log('--------------------------------');
    return null; // TODO: Or a loading spinner
  }

  const heroSectionPageData: HeroSectionContent = pageData.sections.find(section => section.type === 'hero')?.content as HeroSectionContent;
  const ctaSectionPageData: CTASectionContent = pageData.sections.find(section => section.type === 'call-to-action')?.content as CTASectionContent;
  const navbarSectionPageData: NavbarSectionContent = pageData.sections.find(section => section.type === 'navbar')?.content as NavbarSectionContent;
  const brandInfoSectionPageData: BrandInfoSectionContent = pageData.sections.find(section => section.type === 'brand-info')?.content as BrandInfoSectionContent;
  const serviceCategorySectionPageData: ServiceCategorySectionContent = { categories: pageData.sections.find(section => section.type === 'service-category')?.content } as ServiceCategorySectionContent
  const beautyDevicesSectionPageData: BeautyDeviceSectionContent = { beautyDevices: pageData.sections.find(section => section.type === 'beauty-device')?.content } as BeautyDeviceSectionContent
  const popUpSectionPageData: PopUpSectionContent = pageData.sections.find(section => section.type === 'pop-up')?.content as PopUpSectionContent

  return (
    <div className="bg-bg-light-primary flex flex-col items-center section-gaps relative">

      {showCartPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <ModalContainer>
            <Popup
              // image={isLg ? "https://picsum.photos/510/260?random=1" : "https://picsum.photos/300/150?random=1"}
              image={popUpSectionPageData.popupImage.url}
              imageAlt={popUpSectionPageData.popupImageAltText}
              heading={popUpSectionPageData.popupTitle}
              text={popUpSectionPageData.popupDescription}
              button={popUpSectionPageData.popupButtonText}
              hint={popUpSectionPageData.popupDisclaimerText}
              onClose={() => setShowCartPopup(false)}
            />
          </ModalContainer>
        </div>
      )}

      <HeroSection
        title={language === 'arabic' ? "خدماتنا" : "Our services"}
        paragraph={language === 'arabic' ? "اكتشفوا العلاجات التحويلية غير الجراحية التي تكشف عن جمالكم الطبيعي من خلال الرعاية الشخصية والتكنولوجيا المتطورة." : "Discover transformative, non-invasive treatments that reveal your natural beauty through personalized care and cutting-edge technology."}
        // imageSrc="./hero-image.webp"
        imageSrc={heroSectionPageData.image.url}
        imageAlt={heroSectionPageData.imageAltText}
        language={language}
        titleFont="big"
      />

      {isTb && <AnchorNavWrapper serviceCategorySectionPageData={serviceCategorySectionPageData}/>}

      <NavbarContainer
        language={language}
        setLanguage={setLanguage}
        setShowCartPopup={setShowCartPopup}
        promoBarText={navbarSectionPageData.promoBarText}
        promoBarVisibility={navbarSectionPageData.promoBarVisibility}
        onJoinRiviveClick={() => animatedNavigate('/signup')}
        brandInfo={brandInfoSectionPageData}
      />

      <FacilitiesSection
        heading={language === 'arabic' ? "انطلقوا إلى عالم يجمع بين العناية المخصصة والرعاية الفائقة ليكشف عن جمالكم الطبيعي المتألق" :"Step into a world where personalized treatments and exceptional care merge for your natural beauty transformation."}
        subheading={language === 'arabic' ? "كل التفاصيل مصممة خصيصًا لراحتك" : "Every detail crafted for your comfort"}
        language={language}
        facilities={language === 'arabic' ? [
          { label: 'غرف علاج خاصة', icon: EyeSlash },
          { label: 'الوصول إلى المنتجات المميزة (قريبًا)', icon: HandSoap },
          { label: 'مستلزمات الضيوف متوفرة', icon: Gift },
          { label: 'صالة استرخاء', icon: Armchair },
          { label: 'مرطّبات مجانية', icon: Coffee },
          { label: 'غرف تبديل الملابس', icon: CoatHanger },
          { label: 'خزائن آمنة للعملاء', icon: Lockers },
          { label: 'دشات خاصة', icon: Shower },
          { label: 'دخول ومواقف VIP', icon: LetterCircleP },
          { label: 'معايير نظافة ذات مستوى عالمي', icon: ShieldStar },
        ] : [
          { label: 'Private treatment rooms', icon: EyeSlash },
          { label: 'Access to premium products (soon)', icon: HandSoap },
          { label: 'Provided guest essentials', icon: Gift },
          { label: 'Leisure lounge', icon: Armchair },
          { label: 'Complimentary refreshments', icon: Coffee },
          { label: 'Changing rooms', icon: CoatHanger },
          { label: 'Secure client lockers', icon: Lockers },
          { label: 'Private showers', icon: Shower },
          { label: 'VIP access & parking', icon: LetterCircleP },
          { label: 'World-class hygiene standards', icon: ShieldStar },
        ]}
      />

      {serviceCategorySectionPageData.categories.sort((a, b) => a.displayOrder - b.displayOrder).map((category) => (


        <div key={category.serviceCategoryTitle} className="w-full flex flex-col items-center section-gaps relative">

          <ServiceIntroductionSection
            imageSrc={category.serviceCategoryImage.url}
            imageAlt={category.serviceCategoryImageAltText}
            heading={category.serviceCategoryTitle}
            navigationLabel={category.serviceCategoryNavigationLabel}
            subheading={category.serviceCategorySectionSubheading}
            benefits={[
              { title: category.benefitsColumnTitle1, content: category.benefitsColumnDescription1 },
              { title: category.benefitsColumnTitle2, content: category.benefitsColumnDescription2 },
              { title: category.benefitsColumnTitle3, content: category.benefitsColumnDescription3 }
            ]}
          />

          {category.serviceCategorySubservices.length > 0 && (
            <OfferedServicesSection
              subservices={category.serviceCategorySubservices}
              headlineText={category.serviceCategoryHeading}
              subheadingText={category.serviceCategorySubheading}
              paragraphText={category.serviceCategoryHeadingDescription}
              language={language}
            />
          )}


          {category.serviceCategoryTestimonials.length > 0 && (
            <TestimonialCard testimonials={category.serviceCategoryTestimonials} addTopPadding={false} language={language}/>
          )}

        </div>
      ))}

      <DevicesSection
        beautyDevicesSectionPageData={beautyDevicesSectionPageData}
        headlineText={language === 'arabic' ? "التقنية الجمالية خلف إشراقتكم" : "The beauty technology behind your glow"}
        subheadingText={language === 'arabic' ? "التقنية الجمالية الحصرية" : "EXCLUSIVE BEAUTY-TECH"}
        paragraphText={language === 'arabic' ? "اختبروا نتائج تحويلية مع أجهزتنا الحصرية في المملكة العربية السعودية في مجال التجميل، لتقدّم علاجات دقيقة وغير جراحية مصممة لإبراز إشراقتكم الطبيعية." : "Experience transformative results with our KSA-exclusive beauty-tech devices, delivering precise, non-invasive treatments tailored to reveal your natural radiance."}
        language={language}
      />

      <FooterWrapper
        ctaSectionPageData={ctaSectionPageData}
        brandInfoSectionPageData={brandInfoSectionPageData}
        language={language}
        setLanguage={setLanguage}
      />
    </div>
  )
}
